import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { overlayCardClass, overlayCardSizes, overlayScrimClass } from '@/lib/overlayStyles';
import { IconButton } from '@/components/ui/button';

export interface OverlayPanelProps {
  onClose: () => void;
  children: ReactNode;
  title?: string;
  size?: keyof typeof overlayCardSizes;
  className?: string;
}

export default function OverlayPanel({ onClose, children, title, size = 'xl', className }: OverlayPanelProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('no-scroll');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('no-scroll');
    };
  }, [onClose]);

  const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return createPortal(
    <motion.div
      className={cn(overlayScrimClass)}
      onClick={onClose}
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      <motion.div
        className={cn(overlayCardClass, overlayCardSizes[size], className)}
        onClick={stopPropagation}
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 12 }}
        transition={{ duration: 0.22, ease: [0.18, 0.75, 0.25, 1] }}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-white/8 px-4 py-3">
          {title ? (
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/45">{title}</p>
          ) : (
            <span />
          )}
          <IconButton
            onClick={onClose}
            variant="ghost-light"
            size="icon"
            className="rounded-lg"
            icon={X}
            aria-label="Close"
          />
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-4">{children}</div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
